/** @format */

import { useState, useEffect } from "react";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import toast from "react-hot-toast";

export const useWithdrawals = (options = {}) => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all withdrawals
  const fetchWithdrawals = async () => {
    try {
      setLoading(true);

      let q = query(
        collection(db, "withdrawals"),
        orderBy("createdAt", "desc")
      );

      // If filtering by status
      if (options.status) {
        q = query(q, where("status", "==", options.status));
      }

      // If filtering by user
      if (options.userId) {
        q = query(q, where("userId", "==", options.userId));
      }

      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setWithdrawals(docs);
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error(`Error fetching withdrawals: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Add new withdrawal request
  const addWithdrawal = async (data) => {
    try {
      const withdrawalData = {
        ...data,
        status: "pending",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(
        collection(db, "withdrawals"),
        withdrawalData
      );
      toast.success("Withdrawal request submitted successfully");
      fetchWithdrawals(); // Refresh the list
      return docRef.id;
    } catch (err) {
      toast.error(`Error submitting withdrawal: ${err.message}`);
      throw err;
    }
  };

  // Approve withdrawal (admin only)
  const approveWithdrawal = async (withdrawalId, adminUserId) => {
    try {
      const withdrawalRef = doc(db, "withdrawals", withdrawalId);
      await updateDoc(withdrawalRef, {
        status: "approved",
        approvedBy: adminUserId,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast.success("Withdrawal approved successfully");
      fetchWithdrawals(); // Refresh the list
    } catch (err) {
      toast.error(`Error approving withdrawal: ${err.message}`);
      throw err;
    }
  };

  // Reject withdrawal (admin only)
  const rejectWithdrawal = async (
    withdrawalId,
    adminUserId,
    rejectionReason
  ) => {
    try {
      const withdrawalRef = doc(db, "withdrawals", withdrawalId);
      await updateDoc(withdrawalRef, {
        status: "rejected",
        approvedBy: adminUserId,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        rejectionReason: rejectionReason || "No reason provided",
      });

      toast.success("Withdrawal rejected successfully");
      fetchWithdrawals(); // Refresh the list
    } catch (err) {
      toast.error(`Error rejecting withdrawal: ${err.message}`);
      throw err;
    }
  };

  // Update withdrawal
  const updateWithdrawal = async (id, data) => {
    try {
      const withdrawalRef = doc(db, "withdrawals", id);
      await updateDoc(withdrawalRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });

      toast.success("Withdrawal updated successfully");
      fetchWithdrawals(); // Refresh the list
    } catch (err) {
      toast.error(`Error updating withdrawal: ${err.message}`);
      throw err;
    }
  };

  // Delete withdrawal (admin only)
  const deleteWithdrawal = async (id) => {
    try {
      await deleteDoc(doc(db, "withdrawals", id));
      toast.success("Withdrawal deleted successfully");
      fetchWithdrawals(); // Refresh the list
    } catch (err) {
      toast.error(`Error deleting withdrawal: ${err.message}`);
      throw err;
    }
  };

  // Get withdrawal by ID
  const getWithdrawal = async (id) => {
    try {
      const docRef = doc(db, "withdrawals", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        return null;
      }
    } catch (err) {
      toast.error(`Error fetching withdrawal: ${err.message}`);
      throw err;
    }
  };

  // Real-time listener for withdrawals
  useEffect(() => {
    let q = query(collection(db, "withdrawals"), orderBy("createdAt", "desc"));

    if (options.status) {
      q = query(q, where("status", "==", options.status));
    }

    if (options.userId) {
      q = query(q, where("userId", "==", options.userId));
    }

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const docs = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setWithdrawals(docs);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [options.status, options.userId]);

  return {
    withdrawals,
    loading,
    error,
    fetchWithdrawals,
    addWithdrawal,
    approveWithdrawal,
    rejectWithdrawal,
    updateWithdrawal,
    deleteWithdrawal,
    getWithdrawal,
  };
};
